import { useEffect, useState, type ReactNode } from 'react';
import { Search, X } from 'lucide-react';
import { companyRequest } from '@/company/api';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { useAppCatalogLocale } from '@/i18n/context';
import { formatCount } from '@/i18n/format';
import pageCatalog from '@/i18n/ns/connections-page';
import { WorkspaceIcon } from './icons';
import { PipedreamAppCard } from './pipedream-app-card';
import { usePipedreamCatalogue } from './use-pipedream-catalogue';
import {
  isPipedreamApp,
  type CatalogueEntry,
  type CustomerAppEntry,
  type PipedreamApp,
  type PostgresEntry,
  type ScopedConnectionPresentation,
} from './pipedream-catalogue-api';
import { isConnecting, type ScopedConnectionItem } from './connection-api';
import {
  CatalogueCard,
  connectedTileAction,
  tileStateFor,
  type CatalogueConnection,
  type CatalogueTileState,
} from './catalogue-card';
import { CustomerAppCard } from './customer-app-catalogue';
import { NativePostgresCard, NativePostgresSetup } from './native-postgres-setup';
import { ShopifyDevAppCard, useShopifyDevAppSheet } from './custom-mcp/shopify-catalogue';
import { ConnectionMark } from './your-connections/connection-mark';
import { rememberAppLogos } from './your-connections/use-app-logos';
export {
  itemFor,
  type PipedreamApp,
  type ScopedConnectionPresentation,
} from './pipedream-catalogue-api';

/** The Connections page's other ways in: an MCP server, a database, assistants that use Poneva. */
export type OtherWays = {
  canAddServer: boolean;
  onAddServer: () => void;
  onOpenAssistants?: () => void;
};

type Cell = { key: string; connected: boolean; node: ReactNode };

/** A native account offered for connecting matches the search by what its tile shows. */
const matches = (item: ScopedConnectionItem, query: string) =>
  `${item.name} ${item.description}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase());

export function PipedreamCatalogue({
  onConnect,
  busy,
  companyName,
  canConnect = true,
  compact = false,
  preferredApps = [],
  connectedApps = [],
  connectionFor,
  onOpenCustomerApps,
  suggestionLimit = 6,
  pipedreamAvailable = true,
  openNative = false,
  onNativeSaved,
  onTryInChat,
  scopeKey = '',
  refreshKey = 0,
  onConnectionAdded,
  otherWays,
  onOpenShopify,
  nativeItems = [],
  onActivateNative,
  footer,
}: {
  onConnect: (item: ScopedConnectionPresentation) => void;
  busy: string | null;
  companyName: string;
  canConnect?: boolean;
  compact?: boolean;
  preferredApps?: string[];
  /** Provider app slugs this person has connected (onboarding, without the page's own rows). */
  connectedApps?: string[];
  /** The person's own account behind an entry, as the Connections page knows it. */
  connectionFor?: (entry: CatalogueEntry) => CatalogueConnection | null;
  /** Opens a customer-owned app's setup (Meta Ads), with each way it can be connected. */
  onOpenCustomerApps?: (items: ScopedConnectionItem[]) => void;
  suggestionLimit?: number;
  pipedreamAvailable?: boolean;
  openNative?: boolean;
  onNativeSaved?: (item: import('./connection-read-access/api').ConnectionReadItem) => void;
  onTryInChat?: (prompt: string) => void;
  scopeKey?: string;
  /** A change re-reads the entries on screen, e.g. after a store was removed elsewhere. */
  refreshKey?: number;
  /** A Poneva-run connection was added from a card; the page refreshes what lists it. */
  onConnectionAdded?: (notice: string) => void;
  otherWays?: OtherWays;
  /** The page's own Shopify Developer App sheet; without it the catalogue hosts one. */
  onOpenShopify?: (audience: 'private' | 'company', canConnectCompany: boolean) => void;
  /** Services offered through the native plane (installations without Pipedream), one per service. */
  nativeItems?: { item: ScopedConnectionItem; connection: CatalogueConnection | null }[];
  onActivateNative?: (item: ScopedConnectionItem) => void;
  /** Closing content under the catalogue, such as the page's ask-in-chat row. */
  footer?: ReactNode;
}) {
  const copy = useNamespaceCopy(pageCatalog);
  const locale = useAppCatalogLocale() ?? 'en';
  const [query, setQuery] = useState('');
  const [browse, setBrowse] = useState(!compact);
  const [changing, setChanging] = useState<string | null>(null);
  const [changeError, setChangeError] = useState<string | null>(null);
  const [manageMode, setManageMode] = useState(false);
  const [nativeOpen, setNativeOpen] = useState<'private' | 'company' | null>(null);
  const [added, setAdded] = useState(0);
  const [addedNotice, setAddedNotice] = useState('');
  const [postgres, setPostgres] = useState<PostgresEntry | null>(null);
  const suggested = compact && !browse && !query.trim();
  const searching = Boolean(query.trim());
  // Onboarding (compact) hosts no customer-app setup form, so it leaves those entries out.
  const catalogue = usePipedreamCatalogue(
    query,
    suggested,
    preferredApps,
    refreshKey + added,
    compact,
  );
  const { result, total, loading, error, loadingMore, pageError } = catalogue;
  useEffect(() => {
    if (!result) return;
    rememberAppLogos(result.apps);
    const entry = result.apps.find(
      (candidate): candidate is PostgresEntry => candidate.provider === 'postgres',
    );
    if (entry) setPostgres(entry);
  }, [result]);
  // One list from the server, Poneva-run entries first.
  const own = (result?.apps ?? []).filter((entry) => !isPipedreamApp(entry));
  const providerApps = (result?.apps ?? []).filter(isPipedreamApp);
  const apps = suggested ? providerApps.slice(0, suggestionLimit) : providerApps;
  const customerApps = own.filter(
    (entry): entry is CustomerAppEntry => entry.provider === 'customer_app',
  );
  const shopifyEntry = own.find((entry) => entry.provider === 'shopify_dev_app');
  const shopifyCompany =
    shopifyEntry?.provider === 'shopify_dev_app' && Boolean(shopifyEntry.setup.canConnectCompany);
  const ownShopify = useShopifyDevAppSheet({
    companyName,
    canConnectCompany: shopifyCompany,
    // The page shows the notice where it lists the store; without one (onboarding), the catalogue does.
    onConnected: (notice) => {
      setAdded((value) => value + 1);
      if (onConnectionAdded) onConnectionAdded(notice);
      else setAddedNotice(notice);
    },
  });
  const openShopify = (audience: 'private' | 'company') =>
    onOpenShopify ? onOpenShopify(audience, shopifyCompany) : ownShopify.open(audience);
  const postgresReady = Boolean(postgres?.setup.available && postgres.setup.canManage);
  useEffect(() => {
    if (openNative && postgresReady) setNativeOpen('private');
  }, [openNative, postgresReady]);
  const canManage = !compact && Boolean(result?.canManage);
  const managing = manageMode && canManage;

  async function setEnabled(app: PipedreamApp) {
    if (changing) return;
    setChanging(app.slug);
    setChangeError(null);
    try {
      const enabled = app.enabled === false;
      await companyRequest(
        `/api/v1/company/pipedream/apps/${encodeURIComponent(app.slug)}/availability`,
        'PUT',
        { enabled },
      );
      catalogue.updateApp(app.slug, { enabled });
    } catch {
      setChangeError(formatCopy(copy.allowFailed, { name: app.name }));
    } finally {
      setChanging(null);
    }
  }

  const pipedreamConnection = (app: PipedreamApp): CatalogueConnection | null =>
    connectionFor
      ? connectionFor(app)
      : connectedApps.includes(app.slug)
        ? { state: 'connected', identity: null }
        : null;
  // Customer-owned apps of one kind (Meta's two) share one tile.
  const customerGroups = [
    ...new Map(
      customerApps.map((entry) => [
        entry.item.appSetup?.kind ?? entry.slug,
        customerApps.filter(
          (other) =>
            (other.item.appSetup?.kind ?? other.slug) === (entry.item.appSetup?.kind ?? entry.slug),
        ),
      ]),
    ).values(),
  ];
  const cells: Cell[] = [
    ...customerGroups.map((entries) => {
      const connection =
        entries.map((entry) => connectionFor?.(entry) ?? null).find(Boolean) ?? null;
      return {
        key: `customer:${entries[0].slug}`,
        connected: Boolean(connection),
        node: (
          <CustomerAppCard
            key={`customer:${entries[0].slug}`}
            entries={entries}
            busy={busy}
            connection={connection}
            onOpen={(items) => (onOpenCustomerApps ?? ((all) => onConnect(all[0])))(items)}
          />
        ),
      };
    }),
    ...own.flatMap((entry): Cell[] => {
      if (entry.provider === 'shopify_dev_app') {
        const connection = connectionFor?.(entry) ?? null;
        return [
          {
            key: 'shopify_dev_app',
            connected: entry.connected,
            node: (
              <ShopifyDevAppCard
                key="shopify_dev_app"
                entry={entry}
                connection={connection}
                busy={Boolean(busy)}
                onOpen={openShopify}
              />
            ),
          },
        ];
      }
      // On the Connections page a database is one of the other ways in, unless it is searched for.
      if (entry.provider === 'postgres' && (compact || searching || !otherWays))
        return [
          {
            key: 'postgres',
            connected: false,
            node: (
              <NativePostgresCard
                key="postgres"
                entry={entry}
                busy={Boolean(busy)}
                onOpen={setNativeOpen}
              />
            ),
          },
        ];
      return [];
    }),
    ...nativeItems
      .filter(({ item }) => !query.trim() || matches(item, query))
      .map(({ item, connection }) => {
        const state: CatalogueTileState = isConnecting(busy, item)
          ? 'busy'
          : connection
            ? tileStateFor(connection.state)
            : item.actions.connect.available
              ? 'connect'
              : 'unavailable';
        const action =
          state === 'busy'
            ? { label: copy.tileConnecting, onClick: () => undefined }
            : connection
              ? connectedTileAction(state, connection, copy, Boolean(busy))
              : {
                  label: state === 'connect' ? copy.tileConnect : copy.tileUnavailable,
                  disabled: state !== 'connect' || Boolean(busy),
                  onClick: () => onActivateNative?.(item),
                };
        return {
          key: `native:${item.id}`,
          connected: Boolean(connection),
          node: (
            <CatalogueCard
              key={`native:${item.id}`}
              source="native"
              logoId={item.id}
              name={item.name}
              subtitle={connection?.identity ?? item.description}
              description={item.description}
              state={state}
              action={action}
            />
          ),
        };
      }),
    ...apps.map((app) => {
      const connection = pipedreamConnection(app);
      return {
        key: app.slug,
        connected: Boolean(connection),
        node: (
          <PipedreamAppCard
            key={app.slug}
            app={app}
            busy={busy}
            configured={Boolean(result?.configured)}
            canConnect={canConnect}
            companyName={companyName}
            connection={connection}
            manage={managing}
            changing={Boolean(changing)}
            onConnect={onConnect}
            onToggle={() => void setEnabled(app)}
          />
        ),
      };
    }),
  ];
  const mine = searching ? cells.filter((cell) => cell.connected) : [];
  const rest = searching ? cells.filter((cell) => !cell.connected) : cells;
  const resultsLine = loading
    ? copy.searching
    : error && !cells.length
      ? copy.searchUnavailable
      : searching
        ? formatCopy(copy.resultsFor, {
            count: formatCount(copy.resultsNoun, result?.totalCount ?? cells.length, locale),
            query: query.trim(),
          })
        : suggested
          ? copy.selectedForFocus
          : copy.popularFirst;

  return (
    <section
      className={'ws-pipedream-catalogue' + (compact ? ' is-compact' : '')}
      aria-labelledby="ws-catalogue-title"
    >
      <div className="ws-conn-section-head">
        <div className="ws-conn-section-title">
          <h2 id="ws-catalogue-title">{compact ? copy.compactTitle : copy.addTitle}</h2>
          {total !== null && <span>{formatCount(copy.integrations, total, locale)}</span>}
        </div>
        {canManage && (
          <button
            type="button"
            className="ws-text-button ws-conn-manage"
            aria-pressed={managing}
            onClick={() => setManageMode((value) => !value)}
          >
            {!managing && <WorkspaceIcon name="settings" size={14} />}
            {managing ? copy.manageDone : copy.manageMembers}
          </button>
        )}
      </div>
      {managing && <p className="ws-conn-note ws-conn-manage-hint">{copy.manageHint}</p>}
      {/* The clear button sits beside the field's label, so the field is named only by it. */}
      <div className="ws-pipedream-search-bar">
        <label className="ws-pipedream-search-field">
          <Search size={18} aria-hidden="true" />
          <input
            type="search"
            aria-label={copy.searchLabel}
            maxLength={100}
            value={query}
            placeholder={copy.searchPlaceholder}
            onInput={(event) => {
              setQuery(event.currentTarget.value);
              setBrowse(true);
            }}
            onChange={() => undefined}
          />
        </label>
        {query && (
          <button type="button" aria-label={copy.clearSearch} onClick={() => setQuery('')}>
            <X size={15} />
          </button>
        )}
      </div>
      <div className="ws-pipedream-heading">
        {compact ? (
          <div className="ws-pipedream-browse-options">
            <button
              type="button"
              className="ws-pipedream-filter"
              aria-pressed={suggested}
              onClick={() => {
                setBrowse(false);
                setQuery('');
              }}
            >
              {copy.forYou}
            </button>
            <button
              type="button"
              className="ws-pipedream-filter"
              aria-pressed={!suggested}
              onClick={() => {
                setBrowse(true);
                setQuery('');
              }}
            >
              {copy.browseAll}
            </button>
          </div>
        ) : (
          <span />
        )}
        <span className="ws-pipedream-results" role="status">
          {resultsLine}
        </span>
      </div>
      {result && !result.configured && pipedreamAvailable && (
        <p className="ws-pipedream-empty" role="status">
          {copy.searchUnavailable}
        </p>
      )}
      {addedNotice && (
        <p className="ws-pipedream-notice" role="status">
          {addedNotice}
        </p>
      )}
      {(error || changeError) && (
        <div className="ws-pipedream-error" role="alert">
          <span>{error || changeError}</span>
          <button
            type="button"
            className="ws-text-button"
            onClick={() => {
              setChangeError(null);
              catalogue.retry();
            }}
          >
            {copy.retry}
          </button>
        </div>
      )}
      {loading ? (
        <div
          className="ws-pipedream-grid ws-pipedream-skeleton"
          role="status"
          aria-label={copy.searching}
        >
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index}>
              <span />
              <span />
            </div>
          ))}
        </div>
      ) : cells.length ? (
        <>
          {mine.length > 0 && (
            <div className="ws-pipedream-group" aria-label={copy.inYours}>
              <h3 className="ws-pipedream-group-label">
                {copy.inYours}
                <span>{mine.length}</span>
              </h3>
              <div className="ws-pipedream-grid">{mine.map((cell) => cell.node)}</div>
            </div>
          )}
          {rest.length > 0 && (
            <div className="ws-pipedream-group" aria-label={searching ? copy.addNew : undefined}>
              {mine.length > 0 && (
                <h3 className="ws-pipedream-group-label">
                  {copy.addNew}
                  <span>{rest.length}</span>
                </h3>
              )}
              <div className="ws-pipedream-grid">{rest.map((cell) => cell.node)}</div>
            </div>
          )}
        </>
      ) : (
        !error &&
        result?.configured && (
          <div className="ws-pipedream-empty">
            <p>{copy.noResults}</p>
            {query && (
              <button type="button" className="ws-text-button" onClick={() => setQuery('')}>
                {copy.clearSearch}
              </button>
            )}
          </div>
        )
      )}
      <NativePostgresSetup
        key={scopeKey}
        open={Boolean(nativeOpen) && postgresReady}
        initialAudience={nativeOpen ?? 'private'}
        onClose={() => setNativeOpen(null)}
        companyName={companyName}
        canCompany={postgres?.setup.canManage === true}
        scopeKey={scopeKey}
        onSaved={onNativeSaved}
        onTryInChat={onTryInChat}
      />
      {!onOpenShopify && ownShopify.dialog}
      {pageError && (
        <p className="ws-pipedream-error" role="alert">
          {copy.moreFailed}
        </p>
      )}
      {result?.nextCursor && !loading && !suggested && (
        <button
          type="button"
          className="ws-pipedream-load-more"
          disabled={loadingMore}
          onClick={() => void catalogue.loadMore()}
        >
          {loadingMore ? copy.loadingMore : pageError ? copy.showMoreRetry : copy.showMore}
        </button>
      )}
      {otherWays && !compact && (
        <OtherWaysToConnect
          ways={otherWays}
          postgresReady={postgresReady}
          postgresAdminOnly={Boolean(postgres?.setup.available && !postgres.setup.canManage)}
          onDatabase={() => setNativeOpen('private')}
        />
      )}
      {!loading && apps.length > 0 && (
        <p className="ws-pipedream-footnote">
          <WorkspaceIcon name="shield" size={14} />
          {copy.footnote}
        </p>
      )}
      {footer}
    </section>
  );
}

function OtherWaysToConnect({
  ways,
  postgresReady,
  postgresAdminOnly,
  onDatabase,
}: {
  ways: OtherWays;
  postgresReady: boolean;
  /** Databases are set up by an Owner or Admin: a member sees why the tile waits. */
  postgresAdminOnly: boolean;
  onDatabase: () => void;
}) {
  const copy = useNamespaceCopy(pageCatalog);
  const tiles = [
    {
      key: 'server',
      title: copy.serverTitle,
      body: copy.serverBody,
      action: copy.serverAction,
      disabled: !ways.canAddServer,
      onClick: ways.onAddServer,
      mark: <ConnectionMark name={copy.serverTitle} glyph="source" />,
    },
    {
      key: 'database',
      title: copy.databaseTitle,
      body: postgresAdminOnly ? copy.tileAdminSetup : copy.databaseBody,
      action: copy.databaseAction,
      disabled: !postgresReady,
      onClick: onDatabase,
      mark: <ConnectionMark name="PostgreSQL" src={null} logoId="postgresql" />,
    },
    ...(ways.onOpenAssistants
      ? [
          {
            key: 'assistants',
            title: copy.assistantsTitle,
            body: copy.assistantsBody,
            action: copy.assistantsAction,
            disabled: false,
            onClick: ways.onOpenAssistants,
            mark: <ConnectionMark name={copy.assistantsTitle} glyph="agents" />,
          },
        ]
      : []),
  ];
  return (
    <div className="ws-conn-other" aria-labelledby="ws-conn-other-title">
      <h3 id="ws-conn-other-title" className="ws-pipedream-group-label">
        {copy.otherWays}
      </h3>
      <div className="ws-pipedream-grid">
        {tiles.map((tile) => (
          <article
            key={tile.key}
            className="ws-pipedream-card is-other"
            data-state={tile.disabled ? 'unavailable' : 'connect'}
          >
            {tile.mark}
            <div className="ws-pipedream-card-text">
              <h3>{tile.title}</h3>
              <p className="ws-pipedream-subtitle is-wrapping">{tile.body}</p>
            </div>
            <button
              type="button"
              className="ws-pipedream-connect"
              data-state={tile.disabled ? 'unavailable' : 'connect'}
              disabled={tile.disabled}
              onClick={tile.onClick}
            >
              {tile.action}
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}
